import { action, makeObservable, observable, runInAction } from 'mobx';
import { fn } from 'firebase-modules';

import { SessionUserPermission } from '@creative-kit/shared';
import type { RootStore } from '.';

interface AccessListEntry {
  userId: string;
  email: string | null;
  name: string;
  permission: string;
  accessType: 'owner' | 'organization' | 'team' | 'direct' | 'guest';
  grantedAt?: string;
  grantedBy?: {
    userId: string;
    email: string | null;
  };
  teamId?: string;
  teamName?: string;
  expiresAt?: string | null;
  tokenId?: string; // For guest tokens, the token document ID
}

interface SessionAccessList {
  sessionId: string;
  owner: {
    userId: string;
    email: string | null;
    name: string;
  };
  sharingMode: 'private' | 'org' | 'direct' | null;
  organizationId?: string;
  organizationName?: string;
  teamIds?: string[];
  accessList: AccessListEntry[];
}

interface SharingConfig {
  organizationId?: string;
  teamIds?: string[];
}

export class UiSessionSharingStore {
  private rootStore: RootStore;
  private sharingPromise: Promise<unknown> | null = null;

  accessLists: Record<string, SessionAccessList> = {}; // sessionId -> accessList
  isLoading = false;
  isSharing = false;
  error: string | null = null;

  constructor(rootStore: RootStore) {
    makeObservable(this, {
      accessLists: observable,
      isLoading: observable,
      isSharing: observable,
      error: observable,
      loadAccessList: action.bound,
      shareWithOrg: action.bound,
      shareWithTeam: action.bound,
      shareWithUser: action.bound,
      revokeUserAccess: action.bound,
      updateUserPermission: action.bound,
      createGuestLink: action.bound,
      updateGuestShareToken: action.bound,
      updateSharingMode: action.bound,
      getAccessList: action.bound,
    });

    this.rootStore = rootStore;
  }

  /**
   * Load access list for a session
   */
  async loadAccessList(sessionId: string): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const getSessionAccessListFn = fn.httpsCallable('getSessionAccessList');
      const result = await getSessionAccessListFn({ sessionId });

      runInAction(() => {
        this.accessLists[sessionId] = result.data as SessionAccessList;
        this.isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to load access list';
        this.isLoading = false;
      });
    }
  }

  /**
   * Get access list for a session (from cache)
   */
  getAccessList(sessionId: string): SessionAccessList | undefined {
    return this.accessLists[sessionId];
  }

  /**
   * Share session with entire organization
   */
  async shareWithOrg(
    sessionId: string,
    organizationId: string,
    permission: SessionUserPermission = SessionUserPermission.VIEW,
  ): Promise<void> {
    if (this.sharingPromise) {
      await this.sharingPromise; // Await ongoing promise
      return;
    }

    runInAction(() => {
      this.isSharing = true;
      this.error = null;
    });

    this.sharingPromise = (async () => {
      try {
        const shareWithOrgFn = fn.httpsCallable('shareSessionWithOrg');
        const result = await shareWithOrgFn({
          sessionId,
          organizationId,
          permission,
        });

        this.rootStore.uiSnackbarStore.setSnackbarDetails({
          description: result.data.message || 'Session shared with organization successfully',
        });

        // Reload access list
        await this.loadAccessList(sessionId);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to share session with organization';
        this.rootStore.uiSnackbarStore.setSnackbarDetails({
          description: errorMessage,
        });
        runInAction(() => {
          this.error = errorMessage;
        });
        throw err;
      } finally {
        runInAction(() => {
          this.isSharing = false;
          this.sharingPromise = null;
        });
      }
    })();

    await this.sharingPromise;
  }

  /**
   * Share session with a team
   */
  async shareWithTeam(
    sessionId: string,
    organizationId: string,
    teamId: string,
    permission: SessionUserPermission = SessionUserPermission.VIEW,
  ): Promise<{ grantedCount: number }> {
    if (this.sharingPromise) {
      await this.sharingPromise; // Await ongoing promise
      // Return a default value if there's an ongoing promise (shouldn't happen in practice)
      return { grantedCount: 0 };
    }

    runInAction(() => {
      this.isSharing = true;
      this.error = null;
    });

    this.sharingPromise = (async () => {
      try {
        const shareWithTeamFn = fn.httpsCallable('shareSessionWithTeam');
        const result = (await shareWithTeamFn({
          sessionId,
          organizationId,
          teamId,
          permission,
        })) as { data: { grantedCount: number } };

        // Don't show notification here - let the caller handle it for better UX when sharing multiple teams
        // this.rootStore.uiSnackbarStore.setSnackbarDetails({
        //   description: result.data.message || 'Session shared with team successfully',
        // });

        // Reload access list
        await this.loadAccessList(sessionId);

        // Return result data for caller to use
        return {
          grantedCount: result.data.grantedCount ?? 0,
        };
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to share session with team';
        this.rootStore.uiSnackbarStore.setSnackbarDetails({
          description: errorMessage,
        });
        runInAction(() => {
          this.error = errorMessage;
        });
        throw err;
      } finally {
        runInAction(() => {
          this.isSharing = false;
          this.sharingPromise = null;
        });
      }
    })() as Promise<{ grantedCount: number }>;

    return (await this.sharingPromise) as { grantedCount: number };
  }

  /**
   * Share session with a specific user (direct share)
   */
  async shareWithUser(
    sessionId: string,
    userId: string,
    permission: SessionUserPermission = SessionUserPermission.VIEW,
  ): Promise<void> {
    if (this.sharingPromise) {
      await this.sharingPromise; // Await ongoing promise
      return;
    }

    runInAction(() => {
      this.isSharing = true;
      this.error = null;
    });

    this.sharingPromise = (async () => {
      try {
        const shareWithUserFn = fn.httpsCallable('shareSessionWithUser');
        const result = await shareWithUserFn({
          sessionId,
          userId,
          permission,
        });

        this.rootStore.uiSnackbarStore.setSnackbarDetails({
          description: result.data.message || 'Session shared with user successfully',
        });

        // Reload access list
        await this.loadAccessList(sessionId);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to share session with user';
        this.rootStore.uiSnackbarStore.setSnackbarDetails({
          description: errorMessage,
        });
        runInAction(() => {
          this.error = errorMessage;
        });
        throw err;
      } finally {
        runInAction(() => {
          this.isSharing = false;
          this.sharingPromise = null;
        });
      }
    })();

    await this.sharingPromise;
  }

  /**
   * Revoke direct share access for a user
   */
  async revokeUserAccess(sessionId: string, userId: string): Promise<void> {
    if (this.isSharing) return; // Prevent double-clicks

    this.isSharing = true;
    this.error = null;

    try {
      const revokeSessionAccessFn = fn.httpsCallable('revokeSessionAccess');
      const result = await revokeSessionAccessFn({
        sessionId,
        userId,
      });

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: result.data.message || 'Access revoked successfully',
      });

      // Reload access list
      await this.loadAccessList(sessionId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to revoke access';
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
      runInAction(() => {
        this.error = errorMessage;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isSharing = false;
      });
    }
  }

  /**
   * Update permission level for a directly shared user
   */
  async updateUserPermission(
    sessionId: string,
    userId: string,
    permission: SessionUserPermission,
  ): Promise<void> {
    if (this.isSharing) return; // Prevent double-clicks

    this.isSharing = true;
    this.error = null;

    try {
      // Use the same shareWithUser function - it handles updates if permission already exists
      const shareWithUserFn = fn.httpsCallable('shareSessionWithUser');
      const result = await shareWithUserFn({
        sessionId,
        userId,
        permission,
      });

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: result.data.message || 'Permission updated successfully',
      });

      // Reload access list
      await this.loadAccessList(sessionId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update permission';
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
      runInAction(() => {
        this.error = errorMessage;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isSharing = false;
      });
    }
  }

  /**
   * Create guest link (non-CQ user share link)
   */
  async createGuestLink(
    sessionId: string,
    email: string,
    permission: SessionUserPermission = SessionUserPermission.VIEW,
    expiresAt: Date | null = null,
    message?: string,
  ): Promise<void> {
    if (this.isSharing) return; // Prevent double-clicks

    this.isSharing = true;
    this.error = null;

    try {
      const sendSessionShareLinkFn = fn.httpsCallable('sendSessionShareLink');
      const result = await sendSessionShareLinkFn({
        sessionId,
        email,
        permission,
        message,
        expiresAt: expiresAt ? expiresAt.toISOString() : null,
        expirationType: expiresAt === null ? 'never' : 'custom',
      });

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: result.data.message || 'Share link sent successfully',
      });

      // Reload access list
      await this.loadAccessList(sessionId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create guest link';
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
      runInAction(() => {
        this.error = errorMessage;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isSharing = false;
      });
    }
  }

  /**
   * Update guest share token (permission, expiration, or expire now)
   */
  async updateGuestShareToken(
    sessionId: string,
    tokenId: string,
    options: {
      permission?: SessionUserPermission;
      expiresAt?: Date | null;
      expireNow?: boolean;
    },
  ): Promise<void> {
    if (this.isSharing) return; // Prevent double-clicks

    this.isSharing = true;
    this.error = null;

    try {
      const updateGuestShareTokenFn = fn.httpsCallable('updateGuestShareToken');
      const requestData: {
        tokenId: string;
        sessionId: string;
        permission?: SessionUserPermission;
        expiresAt?: string | null;
        expireNow?: boolean;
      } = {
        tokenId,
        sessionId,
      };

      // Only include fields that are actually being updated
      if (options.permission !== undefined) {
        requestData.permission = options.permission;
      }
      if (options.expiresAt !== undefined) {
        requestData.expiresAt = options.expiresAt ? options.expiresAt.toISOString() : null;
      }
      if (options.expireNow !== undefined) {
        requestData.expireNow = options.expireNow;
      }

      const result = await updateGuestShareTokenFn(requestData);

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: result.data.message || 'Guest access updated successfully',
      });

      // Reload access list
      await this.loadAccessList(sessionId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update guest access';
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
      runInAction(() => {
        this.error = errorMessage;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isSharing = false;
      });
    }
  }

  /**
   * Update sharing mode and configuration
   */
  async updateSharingMode(
    sessionId: string,
    mode: 'private' | 'org', // Direct share is handled separately, not a mode
    config: SharingConfig = {},
  ): Promise<void> {
    if (this.isSharing) return; // Prevent double-clicks

    this.isSharing = true;
    this.error = null;

    try {
      const updateSessionSharingFn = fn.httpsCallable('updateSessionSharing');
      const result = await updateSessionSharingFn({
        sessionId,
        mode,
        organizationId: config.organizationId,
        teamIds: config.teamIds,
      });

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: result.data.message || 'Sharing settings updated successfully',
      });

      // Reload access list
      await this.loadAccessList(sessionId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update sharing settings';
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
      runInAction(() => {
        this.error = errorMessage;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isSharing = false;
      });
    }
  }
}
